import { switchClasses, Theme, type Components } from '@mui/material';
import { green } from '../colors';
import { themeVars } from '../themeVars';

export const MuiSwitch: Components<Theme>['MuiSwitch'] = {
  styleOverrides: {
    root: {
      // Between track and thumb
      '--Switch-gap': '2px',
      '--Switch-thumbSize': 'calc(var(--Switch-height) - (var(--Switch-gap) * 2))',
      width: 'var(--Switch-width)',
      height: 'var(--Switch-height)',
      // Original MUI switch uses padding due to switchBase hover which extends beyond
      // switch track boundaries
      padding: 0,
      overflow: 'unset',
    },
    switchBase: ({ theme }) => ({
      padding: 0,
      margin: 'var(--Switch-gap)',
      transitionDuration: `${theme.transitions.duration.standard}ms`,
      overflow: 'unset',
      [`&.${switchClasses.checked}`]: {
        transform: `translateX(var(--Switch-thumbSize))`,
        color: themeVars(theme).palette.common.white,
        [`& + .${switchClasses.track}`]: {
          backgroundColor: green[50],
          opacity: 1,
          border: 0,
        },
        [`&.${switchClasses.disabled} + .${switchClasses.track}`]: {
          opacity: 0.5,
        },
      },
      [`&.Mui-focusVisible + .${switchClasses.track}`]: {
        boxShadow: `0 0 0 1px ${themeVars(theme).palette.background.default}, 0 0 0 3px ${themeVars(theme).palette.border.focused}`,
      },
      [`&.${switchClasses.disabled} .${switchClasses.thumb}`]: {
        color: themeVars(theme).palette.action.disabledBackground,
      },
      [`&.${switchClasses.disabled} + .${switchClasses.track}`]: {
        opacity: 1,
        backgroundColor: themeVars(theme).palette.action.disabledBackground,
      },
      [`&:hover, &.${switchClasses.checked}:hover`]: {
        backgroundColor: 'transparent',
      },
    }),
    thumb: {
      boxSizing: 'border-box',
      boxShadow: 'none',
      width: 'var(--Switch-thumbSize)',
      height: 'var(--Switch-thumbSize)',
    },
    track: ({ theme }) => ({
      borderRadius: 'calc(var(--Switch-height) / 2)',
      backgroundColor: `rgba(${themeVars(theme).palette.action.activeChannel} / 0.32)`,
      ...theme.applyStyles('dark', {
        backgroundColor: `rgba(${themeVars(theme).palette.action.activeChannel} / 0.24)`,
      }),
      opacity: 1,
      transitionDuration: `${theme.transitions.duration.standard}ms`,
    }),
  },
  variants: [
    {
      props: { size: 'medium' },
      style: {
        '--Switch-width': '44px',
        '--Switch-height': '24px',
      },
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        '--Switch-width': '32px',
        '--Switch-height': '18px',
        padding: 0,
        [`& .${switchClasses.thumb}`]: {
          width: 'var(--Switch-thumbSize)',
          height: 'var(--Switch-thumbSize)',
        },
        [`& .${switchClasses.switchBase}`]: {
          padding: 0,
          transitionDuration: `${theme.transitions.duration.short}ms`,
          [`&.${switchClasses.checked}`]: {
            transform: `translateX(var(--Switch-thumbSize))`,
          },
        },
        [`& .${switchClasses.track}`]: {
          transitionDuration: `${theme.transitions.duration.short}ms`,
        },
      }),
    },
  ],
};
